import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Check, Sparkles, Monitor, Smartphone } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PERSONA_ORDER,
  PERSONAS,
  mergePersonaSeeds,
  getWidget,
  type ConfiguredFeatures,
  type DashboardLayout,
  type NormalizeContext,
  type PersonaKey,
  type PlacedWidget,
  type WidgetDescriptor,
  type WidgetWidth,
} from '@pcp/shared';
import { BottomSheet } from '../BottomSheet';
import { Tabs } from '../Tabs';
import { Icon, type IconName } from '../../lib/icons';
import { useAuthStore, useCan } from '../../lib/authStore';
import { useToastStore } from '../../lib/stores';
import { persistLayout } from '../../lib/dashboardStore';
import { buildAiDashboardLayout, getProfile, type UserProfile } from '../../lib/api';

// ─── SetupWizard ──────────────────────────────────────────────────────────────
// Full-screen first-run dashboard setup. Built from the shared primitives only:
// a full-size BottomSheet (size='full') hosting a PageHeader-style title, a Tabs
// stepper, and 7 steps:
//   (a) Welcome → (b) Personas (≥1) → (c) Sources (pre-checked from configured
//   features) → (d) Density → (e) Mobile (which widgets stay on the phone + their
//   phone size) → (f) AI build (HIDDEN entirely without `ai.use`) → (g) Review.
//
// Every step has a "Skip setup" affordance: it writes a persona preset (or an
// empty default) + markOnboarded so a user is never stuck. Finish is ONE request:
// PUT /api/dashboard/layout with markOnboarded:true (persistLayout). On PUT failure
// we DON'T mark onboarded — the wizard simply reappears next load.
//
// CLAUDE.md: no inline @media (the BottomSheet + responsive.css own the layout);
// persona accents are CSS-var token NAMES from index.css (PERSONAS[*].accent), used
// via var(); colours/space all come from index.css tokens.

type StepId = 'welcome' | 'personas' | 'sources' | 'density' | 'mobile' | 'ai' | 'review';

interface StepDef { id: StepId; label: string; }

// Directional slide between steps. `direction` is +1 forward / -1 back; with
// prefers-reduced-motion we collapse the transition duration so it snaps.
const stepVariants = {
  enter: (dir: number) => ({ opacity: 0, x: dir * 28 }),
  center: { opacity: 1, x: 0 },
  exit: (dir: number) => ({ opacity: 0, x: dir * -28 }),
};

/** Build the ordered step list, dropping the AI step when the user lacks ai.use. */
function buildSteps(canUseAi: boolean): StepDef[] {
  const steps: StepDef[] = [
    { id: 'welcome', label: 'Welcome' },
    { id: 'personas', label: 'Personas' },
    { id: 'sources', label: 'Sources' },
    { id: 'density', label: 'Density' },
    { id: 'mobile', label: 'Mobile' },
  ];
  if (canUseAi) steps.push({ id: 'ai', label: 'AI build' });
  steps.push({ id: 'review', label: 'Review' });
  return steps;
}

export function SetupWizard() {
  const queryClient = useQueryClient();
  const can = useCan();
  const canUseAi = can('ai.use');
  const addToast = useToastStore((s) => s.addToast);
  const permissions = useAuthStore((s) => s.user?.permissions);

  // Profile carries the configured-feature flags + jobTitle. The OnboardingGate
  // only mounts us once the profile loaded cleanly, so this is effectively warm.
  const { data: profile } = useQuery<UserProfile>({
    queryKey: ['profile'],
    queryFn: getProfile,
    staleTime: 60_000,
  });
  // Memoize so an absent/loading profile (the `?? {}` branch) doesn't mint a new
  // object each render — which makes the seeding effect below loop infinitely.
  const configuredFeatures: ConfiguredFeatures = useMemo(
    () => profile?.configuredFeatures ?? {},
    [profile?.configuredFeatures],
  );

  const ctx: NormalizeContext = useMemo(
    () => ({ permissions: permissions ?? [], features: configuredFeatures }),
    [permissions, configuredFeatures],
  );

  // ── Wizard working state ──────────────────────────────────────────────────
  const [personas, setPersonas] = useState<PersonaKey[]>([]);
  // Sources the user keeps enabled — seeded from configuredFeatures (all true that
  // the environment supports). Unchecking a source filters its widgets out of the
  // built layout. Always-eligible widgets (no requiredFeature) are unaffected.
  const [enabledFeatures, setEnabledFeatures] = useState<ConfiguredFeatures>({});
  const [density, setDensity] = useState<'comfortable' | 'compact'>('comfortable');
  // Per-widget mobile config (visible + phone width) keyed by widget id, applied
  // over the built layout on finish. Defaults come from the layout itself.
  const [mobileOverrides, setMobileOverrides] = useState<Record<string, { visible: boolean; width: WidgetWidth }>>({});
  const [saving, setSaving] = useState(false);
  const [stepId, setStepId] = useState<StepId>('welcome');
  // Direction of the last navigation (1 = forward, -1 = back) drives the slide.
  const [direction, setDirection] = useState(1);
  const prefersReducedMotion = useReducedMotion();

  const steps = useMemo(() => buildSteps(canUseAi), [canUseAi]);
  const stepIndex = Math.max(0, steps.findIndex((s) => s.id === stepId));

  // Seed enabledFeatures from configuredFeatures once it loads (all on by default).
  useEffect(() => {
    setEnabledFeatures((prev) =>
      Object.keys(prev).length ? prev : { ...configuredFeatures },
    );
  }, [configuredFeatures]);

  // The effective eligibility context for BUILDING the layout: the user's real
  // permissions ∪ the SOURCES they kept enabled (a source they unchecked is
  // treated as not-configured so its widgets are filtered out).
  const buildCtx: NormalizeContext = useMemo(
    () => ({ permissions: permissions ?? [], features: enabledFeatures }),
    [permissions, enabledFeatures],
  );

  // The base layout from persona seeds (the SINGLE default-layout algorithm) — the
  // preview the wizard refines. Rebuilt whenever personas or kept sources change.
  const baseLayout: DashboardLayout = useMemo(
    () => mergePersonaSeeds(personas, buildCtx),
    [personas, buildCtx],
  );

  // An AI-suggested layout (step f) takes precedence over the persona base when set.
  const [aiLayout, setAiLayout] = useState<DashboardLayout | null>(null);
  const effectiveLayout = aiLayout ?? baseLayout;

  // Apply density + the per-widget mobile overrides on top of the effective layout.
  const finalLayout: DashboardLayout = useMemo(() => {
    const widgets: PlacedWidget[] = effectiveLayout.widgets.map((w) => {
      const ov = mobileOverrides[w.id];
      return ov
        ? { ...w, visibleOnMobile: ov.visible, widthMobile: ov.width }
        : w;
    });
    return { ...effectiveLayout, personas, density, widgets };
  }, [effectiveLayout, mobileOverrides, personas, density]);

  // ── Navigation ────────────────────────────────────────────────────────────
  // Navigate with awareness of direction so the step slides in/out the right way.
  const goTo = (id: StepId) => {
    const target = steps.findIndex((s) => s.id === id);
    if (target < 0 || target === stepIndex) return;
    setDirection(target > stepIndex ? 1 : -1);
    setStepId(id);
  };
  const goNext = () => {
    const next = steps[stepIndex + 1];
    if (next) goTo(next.id);
  };
  const goBack = () => {
    const prev = steps[stepIndex - 1];
    if (prev) goTo(prev.id);
  };

  const canAdvance = stepId !== 'personas' || personas.length >= 1;

  // ── Finish / Skip (single PUT with markOnboarded) ─────────────────────────
  const completeWith = async (layout: DashboardLayout) => {
    if (saving) return;
    setSaving(true);
    try {
      await persistLayout(layout, { markOnboarded: true });
      // Refresh the profile so OnboardingGate sees onboardedAt and unmounts us.
      await queryClient.invalidateQueries({ queryKey: ['profile'] });
      addToast({ type: 'success', message: 'Your dashboard is ready.' });
      // No explicit close: the gate unmounts the wizard once the profile reports
      // onboardedAt. (If the gate is bypassed, the success toast still confirms.)
    } catch (e) {
      // DON'T mark onboarded on failure — the wizard reappears next load.
      addToast({ type: 'error', message: e instanceof Error ? e.message : 'Could not save your dashboard.' });
      setSaving(false);
    }
  };

  const onFinish = () => completeWith(finalLayout);
  // Skip writes a persona preset (or the empty default when none picked) so the
  // user is never stuck at the wizard — onboardedAt is stamped either way.
  const onSkip = () => completeWith(mergePersonaSeeds(personas, buildCtx));

  // ── Render ────────────────────────────────────────────────────────────────
  const footer = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
      <button type="button" className="btn btn-sm btn-ghost" onClick={onSkip} disabled={saving} style={{ color: 'var(--text-tertiary)' }}>
        Skip setup
      </button>
      <div style={{ flex: 1 }} />
      {stepIndex > 0 && (
        <button type="button" className="btn btn-sm btn-secondary" onClick={goBack} disabled={saving}>
          Back
        </button>
      )}
      {stepId === 'review' ? (
        <button type="button" className="btn btn-sm btn-primary" onClick={onFinish} disabled={saving}>
          {saving ? 'Saving…' : 'Finish'}
        </button>
      ) : (
        <button type="button" className="btn btn-sm btn-primary" onClick={goNext} disabled={!canAdvance || saving}>
          Continue
        </button>
      )}
    </div>
  );

  return (
    <BottomSheet
      open
      onClose={onSkip}
      size="full"
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <Icon name="dashboard" size={18} /> Set up your dashboard
        </span>
      }
      footer={footer}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        {/* Smooth progress affordance — animates as you advance; reads clearly on a
            phone even when the stepper's active tab has scrolled out of view. */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
            <span style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--text-secondary)' }}>
              Step {stepIndex + 1} of {steps.length}
            </span>
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>{steps[stepIndex]?.label}</span>
          </div>
          <div style={{ height: 4, borderRadius: 999, background: 'var(--border-subtle)', overflow: 'hidden' }}>
            <motion.div
              initial={false}
              animate={{ width: `${Math.round(((stepIndex + 1) / steps.length) * 100)}%` }}
              transition={{ duration: prefersReducedMotion ? 0 : 0.4, ease: 'easeOut' }}
              style={{ height: '100%', background: 'var(--accent-primary)', borderRadius: 999 }}
            />
          </div>
        </div>

        {/* Stepper — clickable; advance via Continue so validation holds. */}
        <Tabs
          tabs={steps.map((s, i) => ({ id: s.id, label: `${i + 1}. ${s.label}` }))}
          active={stepId}
          onChange={(id) => {
            // Only allow jumping BACKWARD or to an already-valid step.
            const target = steps.findIndex((s) => s.id === id);
            if (target <= stepIndex || personas.length >= 1) goTo(id as StepId);
          }}
        />

        {/* Directional slide between steps; the min-height keeps the pinned footer
            from jumping while one step exits and the next enters (mode="wait"). */}
        <div style={{ position: 'relative', minHeight: 300 }}>
          <AnimatePresence mode="wait" custom={direction} initial={false}>
            <motion.div
              key={stepId}
              custom={direction}
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: prefersReducedMotion ? 0 : 0.24, ease: [0.22, 0.61, 0.36, 1] }}
              role="group"
              aria-label={`Step ${stepIndex + 1} of ${steps.length}: ${steps[stepIndex]?.label ?? ''}`}
            >
              {stepId === 'welcome' && <WelcomeStep canUseAi={canUseAi} />}
              {stepId === 'personas' && (
                <PersonasStep personas={personas} onChange={setPersonas} />
              )}
              {stepId === 'sources' && (
                <SourcesStep
                  configuredFeatures={configuredFeatures}
                  enabled={enabledFeatures}
                  onChange={setEnabledFeatures}
                />
              )}
              {stepId === 'density' && <DensityStep density={density} onChange={setDensity} />}
              {stepId === 'mobile' && (
                <MobileStep
                  layout={effectiveLayout}
                  overrides={mobileOverrides}
                  onChange={setMobileOverrides}
                />
              )}
              {stepId === 'ai' && canUseAi && (
                <AiStep
                  personas={personas}
                  density={density}
                  suggested={aiLayout}
                  onSuggested={setAiLayout}
                  onClear={() => setAiLayout(null)}
                />
              )}
              {stepId === 'review' && (
                <ReviewStep layout={finalLayout} personas={personas} density={density} usedAi={!!aiLayout} />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </BottomSheet>
  );
}

// ─── Steps ────────────────────────────────────────────────────────────────────

function StepHeading({ title, hint }: { title: string; hint: string }) {
  return (
    <div style={{ marginBottom: 'var(--space-4)' }}>
      <h2 style={{ margin: 0, fontSize: 'var(--text-lg)', fontWeight: 700, color: 'var(--text-primary)' }}>{title}</h2>
      <p style={{ margin: '4px 0 0', fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)' }}>{hint}</p>
    </div>
  );
}

function WelcomeStep({ canUseAi }: { canUseAi: boolean }) {
  return (
    <div className="glass-card-static" style={{ padding: 'var(--space-6) var(--space-5)', maxWidth: 640 }}>
      <div style={{ display: 'inline-flex', color: 'var(--accent-primary)', marginBottom: 'var(--space-3)' }}>
        <Icon name="dashboard" size={32} />
      </div>
      <h2 style={{ margin: '0 0 var(--space-2)', fontSize: 'var(--text-xl)', fontWeight: 700 }}>
        Let's build your dashboard
      </h2>
      <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
        Pick the roles you identify with and we'll assemble a starting dashboard from the widgets that
        matter to you — inbox, operations, releases, knowledge and more. You can fine-tune everything
        afterwards in edit mode.
        {canUseAi ? ' You can also let AI design the layout for you.' : ''}
      </p>
      <p style={{ margin: 'var(--space-4) 0 0', fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
        This takes about a minute. You can change or reset it any time from Settings.
      </p>
    </div>
  );
}

function PersonasStep({ personas, onChange }: { personas: PersonaKey[]; onChange: (p: PersonaKey[]) => void }) {
  const toggle = (key: PersonaKey) => {
    onChange(personas.includes(key) ? personas.filter((p) => p !== key) : [...personas, key]);
  };
  return (
    <div>
      <StepHeading title="Which roles fit you?" hint="Pick at least one. A widget can belong to several roles — choose all that apply." />
      <div className="onboarding-persona-grid" style={{ display: 'grid', gap: 'var(--space-3)', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
        {PERSONA_ORDER.map((key) => {
          const meta = PERSONAS[key];
          const selected = personas.includes(key);
          return (
            <button
              key={key}
              type="button"
              role="checkbox"
              aria-checked={selected}
              onClick={() => toggle(key)}
              className="glass-card-static"
              style={{
                textAlign: 'left', padding: 'var(--space-3) var(--space-4)', cursor: 'pointer',
                border: `1px solid ${selected ? `var(${meta.accent})` : 'var(--border-subtle)'}`,
                boxShadow: selected ? `0 0 0 1px var(${meta.accent})` : undefined,
                display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-start',
              }}
            >
              <span style={{ display: 'inline-flex', color: `var(${meta.accent})`, flexShrink: 0, marginTop: 2 }}>
                <Icon name={meta.icon as IconName} size={22} />
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                  <span style={{ fontWeight: 600, fontSize: 'var(--text-sm)', color: 'var(--text-primary)' }}>{meta.label}</span>
                  {selected && <Check size={15} style={{ color: `var(${meta.accent})` }} />}
                </span>
                <span style={{ display: 'block', marginTop: 2, fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>{meta.description}</span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Human labels for the configurable sources (WidgetFeature keys). */
const FEATURE_LABELS: Record<string, { label: string; hint: string; icon: IconName }> = {
  aiUsage: { label: 'AI usage', hint: 'Token spend and AI cost metrics.', icon: 'credits' },
  support: { label: 'Support', hint: 'Tickets, support pulse and chat.', icon: 'support' },
  images: { label: 'Image cache', hint: 'ImgProxy cached-image footprint.', icon: 'image' },
  releases: { label: 'Releases', hint: 'Release trains and deploy status.', icon: 'releases' },
  knowledge: { label: 'Knowledge index', hint: 'Embedding / RAG coverage.', icon: 'sources' },
  knowledgeCoverage: { label: 'Extraction coverage', hint: 'Image & attachment extraction.', icon: 'image' },
  documents: { label: 'Documents', hint: 'Documentation knowledge base.', icon: 'documentation' },
  entitlements: { label: 'Entitlements', hint: 'Customer entitlement tracking.', icon: 'entitlements' },
  employees: { label: 'Employees', hint: 'Employee activity profiles.', icon: 'users' },
  insights: { label: 'Insights', hint: 'Org KPIs and AI-impact metrics.', icon: 'insights' },
};

function SourcesStep({
  configuredFeatures, enabled, onChange,
}: {
  configuredFeatures: ConfiguredFeatures;
  enabled: ConfiguredFeatures;
  onChange: (f: ConfiguredFeatures) => void;
}) {
  // Only show sources the ENVIRONMENT actually has configured (the rest can never
  // surface a widget anyway). Pre-checked from configuredFeatures.
  const available = (Object.keys(configuredFeatures) as Array<keyof ConfiguredFeatures>)
    .filter((k) => configuredFeatures[k] === true);
  const toggle = (k: keyof ConfiguredFeatures) => {
    onChange({ ...enabled, [k]: !enabled[k] });
  };
  return (
    <div>
      <StepHeading title="Which sources should feed your dashboard?" hint="Pre-selected from what this workspace has connected. Turn off anything you don't want widgets for." />
      {available.length === 0 ? (
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)' }}>
          No optional sources are configured in this workspace — your dashboard will use the always-available widgets.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {available.map((k) => {
            const meta = FEATURE_LABELS[k] ?? { label: k, hint: '', icon: 'sources' as IconName };
            const on = enabled[k] !== false;
            return (
              <label
                key={k}
                className="glass-card-static"
                style={{
                  display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
                  padding: 'var(--space-2) var(--space-3)', cursor: 'pointer',
                }}
              >
                <span style={{ display: 'inline-flex', color: 'var(--accent-primary)', flexShrink: 0 }}>
                  <Icon name={meta.icon} size={18} />
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text-primary)' }}>{meta.label}</span>
                  {meta.hint && <span style={{ display: 'block', fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>{meta.hint}</span>}
                </span>
                <input type="checkbox" checked={on} onChange={() => toggle(k)} style={{ width: 18, height: 18, accentColor: 'var(--accent-primary)' }} />
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DensityStep({ density, onChange }: { density: 'comfortable' | 'compact'; onChange: (d: 'comfortable' | 'compact') => void }) {
  const opts: Array<{ value: 'comfortable' | 'compact'; label: string; hint: string }> = [
    { value: 'comfortable', label: 'Comfortable', hint: 'More spacing and larger touch targets.' },
    { value: 'compact', label: 'Compact', hint: 'Denser rows — fit more on screen.' },
  ];
  return (
    <div>
      <StepHeading title="How dense should it feel?" hint="You can change this any time." />
      <div style={{ display: 'grid', gap: 'var(--space-3)', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', maxWidth: 520 }}>
        {opts.map((o) => {
          const selected = density === o.value;
          return (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(o.value)}
              className="glass-card-static"
              style={{
                textAlign: 'left', padding: 'var(--space-4)', cursor: 'pointer',
                border: `1px solid ${selected ? 'var(--accent-primary)' : 'var(--border-subtle)'}`,
                boxShadow: selected ? '0 0 0 1px var(--accent-primary)' : undefined,
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontWeight: 600, fontSize: 'var(--text-sm)', color: 'var(--text-primary)' }}>
                {o.label}{selected && <Check size={15} style={{ color: 'var(--accent-primary)' }} />}
              </span>
              <span style={{ display: 'block', marginTop: 4, fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>{o.hint}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MobileStep({
  layout, overrides, onChange,
}: {
  layout: DashboardLayout;
  overrides: Record<string, { visible: boolean; width: WidgetWidth }>;
  onChange: (o: Record<string, { visible: boolean; width: WidgetWidth }>) => void;
}) {
  // Resolve each placed widget's effective mobile config (override → layout value).
  const rows = layout.widgets
    .map((w) => ({ w, d: getWidget(w.id) }))
    .filter((r): r is { w: PlacedWidget; d: WidgetDescriptor } => !!r.d);

  const cfgFor = (w: PlacedWidget) =>
    overrides[w.id] ?? { visible: w.visibleOnMobile, width: w.widthMobile };

  const setVisible = (id: string, w: PlacedWidget, visible: boolean) => {
    onChange({ ...overrides, [id]: { ...cfgFor(w), visible } });
  };
  const setWidth = (id: string, w: PlacedWidget, width: WidgetWidth) => {
    onChange({ ...overrides, [id]: { ...cfgFor(w), width } });
  };

  return (
    <div>
      <StepHeading
        title="What stays on your phone?"
        hint="Keep your phone focused: choose which widgets show on mobile and whether they take half or the full width."
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        {rows.map(({ w, d }) => {
          const cfg = cfgFor(w);
          // Mobile widths collapse to half (small/medium) or full (large/full).
          const half = cfg.width === 'small' || cfg.width === 'medium';
          return (
            <div
              key={w.id}
              className="glass-card-static"
              style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', rowGap: 'var(--space-2)', flexWrap: 'wrap', padding: 'var(--space-2) var(--space-3)' }}
            >
              <span style={{ display: 'inline-flex', color: 'var(--accent-primary)', flexShrink: 0 }}>
                <Icon name={d.icon as IconName} size={18} />
              </span>
              <span style={{ flex: '1 1 140px', minWidth: 0, fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {d.title}
              </span>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-3)', marginLeft: 'auto' }}>
              {cfg.visible && (
                <div role="radiogroup" aria-label="Phone width" style={{ display: 'inline-flex', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={half}
                    onClick={() => setWidth(w.id, w, 'small')}
                    className="btn btn-sm"
                    style={{ border: 'none', borderRadius: 0, background: half ? 'var(--accent-primary-soft)' : 'transparent', color: half ? 'var(--accent-primary)' : 'var(--text-secondary)' }}
                  >
                    Half
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={!half}
                    onClick={() => setWidth(w.id, w, 'full')}
                    className="btn btn-sm"
                    style={{ border: 'none', borderRadius: 0, background: !half ? 'var(--accent-primary-soft)' : 'transparent', color: !half ? 'var(--accent-primary)' : 'var(--text-secondary)' }}
                  >
                    Full
                  </button>
                </div>
              )}
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', cursor: 'pointer' }}>
                On phone
                <input
                  type="checkbox"
                  checked={cfg.visible}
                  onChange={(e) => setVisible(w.id, w, e.target.checked)}
                  style={{ width: 16, height: 16, accentColor: 'var(--accent-primary)' }}
                />
              </label>
              </div>
            </div>
          );
        })}
        {rows.length === 0 && (
          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)' }}>
            Pick at least one persona to see your widgets here.
          </div>
        )}
      </div>
    </div>
  );
}

function AiStep({
  personas, density, suggested, onSuggested, onClear,
}: {
  personas: PersonaKey[];
  density: 'comfortable' | 'compact';
  suggested: DashboardLayout | null;
  onSuggested: (l: DashboardLayout) => void;
  onClear: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [usedFallback, setUsedFallback] = useState(false);
  // Abort the in-flight SSE on unmount (e.g. the wizard closes mid-generation).
  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => () => abortRef.current?.abort(), []);

  const run = async () => {
    if (busy) return;
    setBusy(true); setError(null); setProgress('Starting…'); setUsedFallback(false);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const res = await buildAiDashboardLayout(
        { personas, density },
        { onProgress: setProgress, signal: controller.signal },
      );
      onSuggested(res.layout);
      setUsedFallback(res.source === 'fallback');
    } catch (e) {
      if ((e as Error)?.name === 'AbortError') return; // unmounted — silent
      setError(e instanceof Error ? e.message : 'Could not generate a layout.');
    } finally {
      setBusy(false);
      abortRef.current = null;
    }
  };

  return (
    <div>
      <StepHeading
        title="Let AI build your dashboard"
        hint="Optional. AI uses your personas and role to design a tailored layout. You can still tweak it afterwards."
      />
      <div className="glass-card-static" style={{ padding: 'var(--space-5)', maxWidth: 560 }}>
        {!suggested && (
          <button type="button" className="btn btn-primary" onClick={run} disabled={busy || personas.length === 0} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Sparkles size={16} /> {busy ? 'Designing…' : 'Let AI build my dashboard'}
          </button>
        )}
        {personas.length === 0 && (
          <p style={{ margin: 'var(--space-2) 0 0', fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
            Pick at least one persona first.
          </p>
        )}
        {busy && (
          <p style={{ margin: 'var(--space-3) 0 0', fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>{progress}</p>
        )}
        {error && (
          <p style={{ margin: 'var(--space-3) 0 0', fontSize: 'var(--text-sm)', color: 'var(--accent-danger)' }}>{error}</p>
        )}
        {suggested && (
          <div style={{ marginTop: error ? 'var(--space-3)' : 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text-primary)' }}>
              <Check size={16} style={{ color: 'var(--accent-success)' }} />
              {usedFallback ? 'Built a layout from your personas' : 'AI designed a layout'}
              {' '}({suggested.widgets.length} widgets)
            </div>
            {usedFallback && (
              <p style={{ margin: '4px 0 0', fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                The AI builder was unavailable, so we used a role-based layout instead.
              </p>
            )}
            <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
              <button type="button" className="btn btn-sm btn-secondary" onClick={run} disabled={busy} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Sparkles size={14} /> Regenerate
              </button>
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => { onClear(); setUsedFallback(false); }} disabled={busy} style={{ color: 'var(--text-tertiary)' }}>
                Use my role-based layout instead
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ReviewStep({ layout, personas, density, usedAi }: { layout: DashboardLayout; personas: PersonaKey[]; density: string; usedAi: boolean }) {
  const onMobile = layout.widgets.filter((w) => w.visibleOnMobile).length;
  return (
    <div>
      <StepHeading title="Review & finish" hint="Here's the dashboard we'll set up. You can edit or reset it any time." />
      <div className="glass-card-static" style={{ padding: 'var(--space-4) var(--space-5)', maxWidth: 640 }}>
        <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 'var(--space-2) var(--space-4)', fontSize: 'var(--text-sm)' }}>
          <dt style={{ color: 'var(--text-tertiary)' }}>Roles</dt>
          <dd style={{ margin: 0, color: 'var(--text-primary)' }}>
            {personas.length ? personas.map((p) => PERSONAS[p].label).join(', ') : 'None selected'}
          </dd>
          <dt style={{ color: 'var(--text-tertiary)' }}>Widgets</dt>
          <dd style={{ margin: 0, color: 'var(--text-primary)' }}>{layout.widgets.length} placed</dd>
          <dt style={{ color: 'var(--text-tertiary)' }}>On your phone</dt>
          <dd style={{ margin: 0, color: 'var(--text-primary)' }}>{onMobile} shown</dd>
          <dt style={{ color: 'var(--text-tertiary)' }}>Density</dt>
          <dd style={{ margin: 0, color: 'var(--text-primary)', textTransform: 'capitalize' }}>{density}</dd>
          <dt style={{ color: 'var(--text-tertiary)' }}>Designed by</dt>
          <dd style={{ margin: 0, color: 'var(--text-primary)' }}>{usedAi ? 'AI builder' : 'Role presets'}</dd>
        </dl>
      </div>
      <div style={{ marginTop: 'var(--space-3)', display: 'flex', gap: 'var(--space-3)', color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Monitor size={13} /> {layout.widgets.filter((w) => w.visibleOnDesktop).length} on desktop</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Smartphone size={13} /> {onMobile} on mobile</span>
      </div>
    </div>
  );
}
